/**
 * A customer's purchase orders, and one in full.
 *
 * The detail page is where a PO is settled: their own PO number and document
 * can still be added or replaced while it waits — their ERP often issues the
 * paperwork a day or two after the buyer commits — and then it is paid by card
 * or wire. Paying goes through the same endpoint as an ordinary checkout, so a
 * PO order reaches the team exactly like any other order, carrying both
 * references.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import useFirebaseAuthentication from "../common/hooks/useFirebaseAuthentication";
import PaymentForm from "../components/PaymentForm";
import { ActivityTrail, actorLabel } from "../components/ActivityTrail";
import {
  FileUpload,
  PoCheckout,
  PurchaseOrder,
  PurchaseOrderSummary,
  QuoteFlowConfig,
  downloadPoAttachment,
  listPurchaseOrders,
  loadPurchaseOrder,
  loadQuoteFlowConfig,
  messageOf,
  payPurchaseOrder,
  readFileForUpload,
  updatePurchaseOrder,
} from "./api";

const money = (amount: number, currency: string) =>
  `${currency === "USD" ? "$" : `${currency} `}${amount.toLocaleString(
    "en-US",
    { minimumFractionDigits: 2, maximumFractionDigits: 2 },
  )}`;

const day = (iso: string) => (iso ? iso.slice(0, 10) : "");

const field =
  "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#00062e] focus:outline-none";

const statusText = (po: {
  status: string;
  orderId: string | null;
  paymentMethod?: string | null;
}) => {
  if (po.status === "cancelled") return "Cancelled";
  if (po.status === "ordered") {
    return po.paymentMethod === "wire_transfer"
      ? "Placed — waiting on your wire"
      : "Placed";
  }
  return "Waiting to be paid";
};

export default function MyPurchaseOrders() {
  const { authUser, authIsLoading } = useFirebaseAuthentication();
  const [orders, setOrders] = useState<PurchaseOrderSummary[] | null>(null);
  const [asGuest, setAsGuest] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const data = await listPurchaseOrders();
      setOrders(data.purchaseOrders);
      setAsGuest(data.asGuest);
    } catch (e) {
      setError(messageOf(e));
      setOrders([]);
    }
  }, []);

  useEffect(() => {
    if (authIsLoading) return;
    void load();
  }, [authIsLoading, authUser, load]);

  return (
    <div className="mx-auto mt-8 mb-16 max-w-4xl px-4">
      <h1 className="text-xl font-semibold text-[#00062e]">
        My purchase orders
      </h1>
      <p className="mt-1 max-w-2xl text-sm text-gray-600">
        Quotes you have committed to. Each one carries our reference and, where
        you gave one, the PO number your own system issued. Open one to add your
        PO document or to pay it.
      </p>
      {asGuest && (
        <p className="mt-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-gray-700">
          These are held against this browser.{" "}
          <Link className="text-blue-700 hover:underline" to="/signin">
            Sign in
          </Link>{" "}
          and they move to your account.
        </p>
      )}
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      {orders === null && (
        <p className="mt-4 text-sm text-gray-500">
          Loading your purchase orders...
        </p>
      )}
      {orders !== null && orders.length === 0 && !error && (
        <p className="mt-4 text-sm text-gray-600">
          You have not raised one yet. Open a saved{" "}
          <Link className="text-blue-700 hover:underline" to="/quotes">
            quote
          </Link>{" "}
          and choose Convert to a purchase order.
        </p>
      )}

      <div className="mt-4 flex flex-col gap-3">
        {(orders || []).map((po) => (
          <div key={po.id} className="rounded-xl border border-gray-200 p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <Link
                className="font-medium text-blue-700 hover:underline"
                to={`/purchase-orders/${po.id}`}
              >
                {po.poNumber}
              </Link>
              <span className="font-medium">
                {money(po.totalPrice, po.currency)}
              </span>
            </div>
            <div className="mt-1 text-sm text-gray-600">
              {day(po.createdAt)} · {po.itemCount}{" "}
              {po.itemCount === 1 ? "item" : "items"} · from {po.quoteReference}{" "}
              · {statusText(po)}
            </div>
            {(po.customerPoNumber || po.hasAttachment) && (
              <div className="mt-1 text-xs text-gray-500">
                {po.customerPoNumber
                  ? `Your PO ${po.customerPoNumber}`
                  : "No PO number of yours"}
                {po.hasAttachment ? " · your PO document is on file" : ""}
              </div>
            )}
            {po.createdBy && (
              <div className="mt-1 text-xs text-gray-600">
                Raised by{" "}
                <span className="font-medium text-[#00062e]">
                  {actorLabel(po.createdBy, authUser?.email)}
                </span>{" "}
                ({po.createdBy.email})
              </div>
            )}
            <ActivityTrail
              className="mt-1"
              activity={po.activity}
              viewerEmail={authUser?.email}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export function PurchaseOrderDetail() {
  const { id } = useParams();
  const { authUser, authIsLoading } = useFirebaseAuthentication();
  const [po, setPo] = useState<PurchaseOrder | null>(null);
  const [config, setConfig] = useState<QuoteFlowConfig | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");
  const [customerPoNumber, setCustomerPoNumber] = useState("");
  const [file, setFile] = useState<FileUpload | null>(null);
  const [checkout, setCheckout] = useState<PoCheckout | null>(null);
  const [paid, setPaid] = useState(false);

  // Waits for the sign-in to be restored before asking. On a fresh load of this
  // URL — a bookmark, a new tab, a refresh — the token is not there for the
  // first moments, and an order held against an account is not ours without it.
  useEffect(() => {
    if (!id || authIsLoading) return;
    setError("");
    Promise.all([loadPurchaseOrder(id), loadQuoteFlowConfig()])
      .then(([loaded, flowConfig]) => {
        setPo(loaded);
        setCustomerPoNumber(loaded.customerPoNumber);
        setConfig(flowConfig);
      })
      .catch((e) => setError(messageOf(e)));
  }, [id, authIsLoading, authUser]);

  const currency = po?.totals?.currency || "USD";
  const open = po?.status === "awaiting_payment";

  const pick = async (chosen: File | null) => {
    setError("");
    setFile(chosen ? await readFileForUpload(chosen) : null);
  };

  const saveDetails = async () => {
    if (!po) return;
    setBusy("details");
    setError("");
    setNotice("");
    try {
      const updated = await updatePurchaseOrder(po.id, {
        customerPoNumber,
        attachment: file,
      });
      setPo(updated);
      setFile(null);
      setNotice("Saved against this purchase order.");
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy("");
    }
  };

  const pay = async (method: "credit_card" | "wire_transfer") => {
    if (!po) return;
    setBusy(method);
    setError("");
    try {
      const result = await payPurchaseOrder(po, method);
      setCheckout(result);
      setPo({
        ...po,
        status: "ordered",
        orderId: result.orderId,
        paymentMethod: result.paymentMethod,
      });
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy("");
    }
  };

  // Card payment takes over the page once the intent exists: nothing else on it
  // is actionable while the card details are up.
  if (po && checkout?.clientSecret && !paid) {
    return (
      <div className="mx-auto mt-8 mb-16 max-w-xl px-4">
        <h1 className="text-xl font-semibold text-[#00062e]">
          Pay {po.poNumber}
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          {money(po.totals.totalPrice, currency)}
          {po.customerPoNumber ? ` · your PO ${po.customerPoNumber}` : ""}
        </p>
        <div className="mt-5">
          <PaymentForm
            clientSecret={checkout.clientSecret}
            totalPrice={po.totals.totalPrice}
            currency={currency}
            onPaymentSuccess={() => setPaid(true)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto mt-8 mb-16 max-w-4xl px-4">
      <Link
        className="text-sm text-blue-700 hover:underline"
        to="/purchase-orders"
      >
        ← My purchase orders
      </Link>
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      {!po && !error && (
        <p className="mt-4 text-sm text-gray-500">
          Loading the purchase order...
        </p>
      )}

      {po && (
        <>
          <h1 className="mt-2 text-xl font-semibold text-[#00062e]">
            Purchase order {po.poNumber}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Raised {day(po.createdAt)} from quote{" "}
            <Link
              className="text-blue-700 hover:underline"
              to={`/quotes/${po.quoteId}`}
            >
              {po.quoteReference}
            </Link>{" "}
            · {statusText(po)}
            {po.orderId ? ` · order ${po.orderId}` : ""}
          </p>
          {paid && (
            <p className="mt-3 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
              Paid. Your confirmation is on its way by email, and we quote{" "}
              {po.customerPoNumber
                ? `your PO ${po.customerPoNumber}`
                : po.poNumber}{" "}
              on everything that follows.
            </p>
          )}
          {checkout?.paymentMethod === "wire_transfer" && (
            <p className="mt-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-gray-700">
              Wire instructions are on their way to {po.customer.email}. Send
              the receipt to{" "}
              {checkout.wireTransferRecipientEmail || "our accounts team"} and
              quote {po.poNumber}.
            </p>
          )}
          {notice && <p className="mt-2 text-sm text-green-700">{notice}</p>}
          {po.activity && po.activity.length > 0 && (
            <div className="mt-3 rounded-xl border border-gray-200 p-3">
              <h2 className="text-sm font-semibold text-[#00062e]">Who did what</h2>
              {po.createdBy && (
                <p className="mt-1 text-xs text-gray-600">
                  Raised by {actorLabel(po.createdBy, authUser?.email)} ({po.createdBy.email})
                </p>
              )}
              <ActivityTrail
                className="mt-2"
                activity={po.activity}
                viewerEmail={authUser?.email}
                startOpen
              />
            </div>
          )}

          <table className="mt-5 min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                <th className="py-2 pr-4">Item</th>
                <th className="py-2 pr-4">Quantity</th>
                <th className="py-2 pr-4">Ship to</th>
                <th className="py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {po.cartItems.map((item, index) => (
                <tr key={index} className="border-b border-gray-100">
                  <td className="py-2 pr-4">
                    {item.product?.summary || "Item"}
                    {item.millCertRequired && (
                      <div className="text-xs text-gray-500">
                        Mill certificate required
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-4">
                    {item.quantityRequiredString ||
                      (item.quantityRequired
                        ? `${item.quantityRequired.value} ${item.quantityRequired.unit}`
                        : "")}
                  </td>
                  <td className="py-2 pr-4 text-gray-600">
                    {[
                      item.deliveryAddress?.city,
                      item.deliveryAddress?.stateOrProvinceCode,
                      item.deliveryAddress?.postalCode,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  </td>
                  <td className="py-2 text-right">
                    {/* Server-computed: price unit converted to the quantity
                        unit (per-lb price x piece weight), so lines sum to the
                        subtotal. Raw multiply only for unconvertible lines. */}
                    {money(
                      typeof item.lineAmount === "number"
                        ? item.lineAmount
                        : (item.product?.price?.value || 0) *
                            (item.quantityRequired?.value || 0),
                      currency,
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-3 flex flex-col items-end gap-1 text-sm">
            <div>
              Subtotal:{" "}
              {money(
                po.totals.productSubtotalBeforeDiscount ||
                  po.totals.productSubtotal,
                currency,
              )}
            </div>
            {po.totals.volumeDiscount && (
              <div>
                Volume discount ({po.totals.volumeDiscount.discountPercent}% on
                qualifying items): −
                {money(po.totals.volumeDiscount.discountAmount, currency)}
              </div>
            )}
            <div>
              Shipping:{" "}
              {po.totals.shippingTotal === 0
                ? "Free"
                : money(po.totals.shippingTotal, currency)}
            </div>
            <div className="text-base font-semibold">
              Total: {money(po.totals.totalPrice, currency)}
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
            <section className="rounded-xl border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-[#00062e]">
                Your own paperwork
              </h2>
              <p className="mt-1 text-xs text-gray-600">
                Your PO number goes on our invoice so your accounts team can
                match it. Your PO document is filed against this order.
              </p>

              {config?.po.askCustomerPoNumber && (
                <label className="mt-3 block">
                  <span className="text-xs text-gray-600">Your PO number</span>
                  <input
                    className={field}
                    name="customer-po-number"
                    value={customerPoNumber}
                    onChange={(e) => setCustomerPoNumber(e.target.value)}
                    disabled={!open}
                  />
                </label>
              )}

              {po.attachment ? (
                <p className="mt-3 text-sm text-gray-700">
                  On file:{" "}
                  <button
                    type="button"
                    className="text-blue-700 hover:underline"
                    onClick={() => void downloadPoAttachment(po)}
                  >
                    {po.attachment.filename}
                  </button>{" "}
                  <span className="text-xs text-gray-500">
                    ({Math.max(1, Math.round(po.attachment.sizeBytes / 1024))}{" "}
                    KB, {day(po.attachment.uploadedAt)})
                  </span>
                </p>
              ) : (
                <p className="mt-3 text-sm text-gray-600">
                  No PO document on file yet.
                </p>
              )}

              {open && config?.po.allowPoUpload && (
                <label className="mt-3 block">
                  <span className="text-xs text-gray-600">
                    {po.attachment ? "Replace it" : "Attach it"}
                  </span>
                  <input
                    className="mt-1 block w-full text-sm text-gray-700 file:mr-3 file:rounded-full file:border-0 file:bg-gray-100 file:px-4 file:py-2 file:text-sm hover:file:bg-gray-200"
                    type="file"
                    name="customer-po-file"
                    accept={config.po.uploadFileTypes
                      .map((type) => `.${type}`)
                      .join(",")}
                    onChange={(e) => void pick(e.target.files?.[0] || null)}
                  />
                </label>
              )}

              {open && (
                <button
                  type="button"
                  className="mt-4 rounded-full border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
                  onClick={() => void saveDetails()}
                  disabled={busy === "details"}
                >
                  {busy === "details" ? "Saving..." : "Save"}
                </button>
              )}
            </section>

            <section className="rounded-xl border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-[#00062e]">Payment</h2>
              {!open ? (
                <p className="mt-1 text-sm text-gray-600">
                  This purchase order has been placed
                  {po.paymentMethod === "wire_transfer"
                    ? " and is waiting on your wire."
                    : "."}
                </p>
              ) : (
                <>
                  <p className="mt-1 text-xs text-gray-600">
                    Nothing is charged until you choose. The quoted price stands
                    until {day(po.quoteValidUntil)}.
                  </p>
                  <div className="mt-4 flex flex-col gap-3">
                    {config?.payment.creditCard && (
                      <button
                        type="button"
                        className="rounded-full bg-[#00062e] px-4 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-50"
                        onClick={() => void pay("credit_card")}
                        disabled={Boolean(busy)}
                      >
                        {busy === "credit_card"
                          ? "Preparing..."
                          : "Pay by card"}
                      </button>
                    )}
                    {config?.payment.wireTransfer && (
                      <button
                        type="button"
                        className="rounded-full border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
                        onClick={() => void pay("wire_transfer")}
                        disabled={Boolean(busy)}
                      >
                        {busy === "wire_transfer"
                          ? "Preparing..."
                          : "Pay by wire transfer"}
                      </button>
                    )}
                    {config?.payment.terms && (
                      <p className="text-xs text-gray-600">
                        Paying on {config.payment.termsDays}-day terms is being
                        set up. Talk to us and we will invoice this order on
                        account.
                      </p>
                    )}
                    {config &&
                      !config.payment.creditCard &&
                      !config.payment.wireTransfer && (
                        <p className="text-sm text-gray-600">
                          We will be in touch to arrange payment for this order.
                        </p>
                      )}
                  </div>
                </>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}
